import { Navigate, Outlet, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { selectUser } from "../store/auth";

// Mirrors the key used in ComputersPage.jsx. Carries an install QR token
// (#apiKey=...&expiresAt=...) across the unauthenticated login round-trip: the
// fragment never reaches the server and must not travel in query params, so it
// is stashed here before the redirect and restored by the Computers page.
const PENDING_INSTALL_TOKEN_KEY = "cyberpass.pendingInstallToken";

/**
 * @param {object} props
 * @param {number[]} props.to
 * @returns {React.Component}
 */
const PrivateRoute = ({ to = [] }) => {
  const user = useSelector(selectUser);
  const location = useLocation();
  console.debug("Hit private route");

  if (!user) {
    console.log("Redirecting to login page");
    stashInstallToken(location);
    // Fragment (and with it the install key) is intentionally excluded from
    // the redirect target — the key is stashed in sessionStorage instead.
    const target = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`/login?redirect_to=${target}`} />;
  }

  if (!to.includes(user.role)) {
    console.log("Access forbitten to current user role");
    return <Navigate to="/" />;
  }

  return <Outlet />;
};

// Helpers

/**
 * Preserve an install QR token found in the URL fragment so it survives the
 * unauthenticated login round-trip. Syncs with `readStashedInstallToken` in
 * ComputersPage.jsx. Silently no-ops when there is no key or storage is
 * unavailable.
 * @param {{ pathname: string, search: string, hash: string }} location
 */
const stashInstallToken = (location) => {
  try {
    const params = new URLSearchParams(location.hash.slice(1));
    const apiKey = params.get("apiKey");
    if (!apiKey) return;
    const rawExpiry = params.get("expiresAt");
    const expiresAt = rawExpiry ? parseInt(rawExpiry, 10) : null;
    sessionStorage.setItem(
      PENDING_INSTALL_TOKEN_KEY,
      JSON.stringify({
        apiKey,
        expiresAt: Number.isFinite(expiresAt) ? expiresAt : null,
      }),
    );
  } catch {
    // Storage unavailable — the key dies with the fragment. The operator can
    // re-open the QR link after logging in.
  }
};

export default PrivateRoute;
